import { useCallback, useMemo, useState } from 'react';
import type {
  TApiApplicationDocument,
  TApplicationDocumentBodyResponse,
  TApplicationDocumentHistoryItem,
} from '~/modules/covenants/types.ts';
import type { StateAidCertificateType } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/stateAidReceived/types.ts';
import { getCertificateDocumentFlags } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/utils.ts';

type TDocumentGenerationSource =
  | { kind: 'new' }
  | { kind: 'historical'; body: unknown }
  | { kind: 'regeneration'; document: TApiApplicationDocument; body?: unknown };

type TUseDocumentGenerationFlowParams = {
  document: TApiApplicationDocument;
  isGenerating: boolean;
  onLoadDocumentBody: (id: number) => Promise<TApplicationDocumentBodyResponse | null>;
};

const NEW_DOCUMENT_SOURCE: TDocumentGenerationSource = { kind: 'new' };

export const useDocumentGenerationFlow = ({
  document,
  isGenerating,
  onLoadDocumentBody,
}: TUseDocumentGenerationFlowParams) => {
  const [isGenerationModalOpen, setIsGenerationModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [source, setSource] = useState<TDocumentGenerationSource>(NEW_DOCUMENT_SOURCE);
  const [stateAidCertificateType, setStateAidCertificateType] = useState<StateAidCertificateType>();

  const resetSource = useCallback(() => {
    setSource(NEW_DOCUMENT_SOURCE);
    setStateAidCertificateType(undefined);
  }, []);

  const openHistory = useCallback(() => {
    resetSource();
    setIsHistoryModalOpen(true);
  }, [resetSource]);

  const openNewDocument = useCallback((type?: StateAidCertificateType) => {
    setSource(NEW_DOCUMENT_SOURCE);
    setStateAidCertificateType(type);
    setIsHistoryModalOpen(false);
    setIsGenerationModalOpen(true);
  }, []);

  const openHistoricalDocument = useCallback(
    async (item: TApplicationDocumentHistoryItem, type?: StateAidCertificateType) => {
      const documentBody = await onLoadDocumentBody(item.id);
      if (!documentBody) return;

      setSource({ kind: 'historical', body: documentBody.body });
      setStateAidCertificateType(type);
      setIsHistoryModalOpen(false);
      setIsGenerationModalOpen(true);
    },
    [onLoadDocumentBody]
  );

  const openRegeneration = useCallback(async () => {
    if (getCertificateDocumentFlags(document.documentId).isStateAidReceived) {
      setSource({ kind: 'regeneration', document });
      setStateAidCertificateType(undefined);
      setIsHistoryModalOpen(true);
      return;
    }

    const sourceId = Number(document.id);
    if (!Number.isFinite(sourceId)) return;

    const documentBody = await onLoadDocumentBody(sourceId);
    if (!documentBody) return;

    setSource({ kind: 'regeneration', document, body: documentBody.body });
    setIsGenerationModalOpen(true);
  }, [document, onLoadDocumentBody]);

  const openRegenerationWithType = useCallback(
    async (type: StateAidCertificateType) => {
      if (source.kind !== 'regeneration') return;

      const sourceId = Number(source.document.id);
      if (!Number.isFinite(sourceId)) return;

      const documentBody = await onLoadDocumentBody(sourceId);
      if (!documentBody) return;

      setSource({ kind: 'regeneration', document: source.document, body: documentBody.body });
      setStateAidCertificateType(type);
      setIsHistoryModalOpen(false);
      setIsGenerationModalOpen(true);
    },
    [onLoadDocumentBody, source]
  );

  const closeGeneration = useCallback(() => {
    if (isGenerating) return;

    setIsGenerationModalOpen(false);
    resetSource();
  }, [isGenerating, resetSource]);

  const closeHistory = useCallback(() => {
    setIsHistoryModalOpen(false);
    resetSource();
  }, [resetSource]);

  const closeGenerationAfterSuccess = useCallback(() => {
    setIsGenerationModalOpen(false);
    resetSource();
  }, [resetSource]);

  const isRegenerationTypeSelection =
    source.kind === 'regeneration' &&
    source.body === undefined &&
    getCertificateDocumentFlags(document.documentId).isStateAidReceived;

  const historicalBody = source.kind === 'new' ? undefined : source.body;
  const regenerationSource = source.kind === 'regeneration' ? source.document : undefined;

  return useMemo(
    () => ({
      isGenerationModalOpen,
      isHistoryModalOpen,
      historicalBody,
      regenerationSource,
      stateAidCertificateType,
      setStateAidCertificateType,
      isRegenerationTypeSelection,
      openHistory,
      openNewDocument,
      openHistoricalDocument,
      openRegeneration,
      openRegenerationWithType,
      closeGeneration,
      closeHistory,
      closeGenerationAfterSuccess,
    }),
    [
      closeGeneration,
      closeGenerationAfterSuccess,
      closeHistory,
      historicalBody,
      isGenerationModalOpen,
      isHistoryModalOpen,
      isRegenerationTypeSelection,
      openHistoricalDocument,
      openHistory,
      openNewDocument,
      openRegeneration,
      openRegenerationWithType,
      regenerationSource,
      stateAidCertificateType,
    ]
  );
};
